import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayClassName, hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HUB_MODAL_OVERLAY_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";

import {
  cancelAlternateOrder,
  fetchOpenAlternateOrders,
  OPEN_ALTERNATE_ORDERS_QUERY_KEY,
  type AlternateOrderRecord,
} from "@/pages/manufacturing/alternateOrderApi";
import { CONFIGURATION_QUEUE_QUERY_KEY } from "@/pages/manufacturing/configurationApi";

const MANAGEMENT_WRITE_HINT = "You do not have permission to cancel alternate orders in Manufacturing management.";

function formatDate(value: string | null): string {
  if (value == null || value.trim() === "") return "Not scheduled";
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatCount(value: number): string {
  return Math.trunc(value).toLocaleString();
}

function fulfillmentLabel(order: AlternateOrderRecord): string {
  if (order.quantityFulfilled <= 0) {
    return `${formatCount(order.quantity)} to build`;
  }
  return `${formatCount(order.quantityFulfilled)} of ${formatCount(order.quantity)} built (${formatCount(order.remainingToBuild)} remaining)`;
}

function useOpenAlternateOrdersState(enabled: boolean) {
  const queryClient = useQueryClient();
  const managementWritesAllowed = useHubModuleWrite("manufacturing_management");
  const [cancelConfirm, setCancelConfirm] = useState<AlternateOrderRecord | null>(null);

  const openOrdersQuery = useQuery({
    queryKey: OPEN_ALTERNATE_ORDERS_QUERY_KEY,
    queryFn: fetchOpenAlternateOrders,
    enabled,
    refetchOnWindowFocus: false,
  });

  const cancelMutation = useMutation({
    mutationFn: (input: { alternateOrderId: number; confirmPartialFulfillment: boolean }) =>
      cancelAlternateOrder(input.alternateOrderId, {
        confirmPartialFulfillment: input.confirmPartialFulfillment,
      }),
    onSuccess: () => {
      setCancelConfirm(null);
      void queryClient.invalidateQueries({ queryKey: OPEN_ALTERNATE_ORDERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: CONFIGURATION_QUEUE_QUERY_KEY });
    },
  });

  return {
    managementWritesAllowed,
    cancelConfirm,
    setCancelConfirm,
    openOrdersQuery,
    cancelMutation,
    orders: openOrdersQuery.data?.orders ?? [],
  };
}

function OpenAlternateOrdersTable({
  orders,
  isLoading,
  isError,
  errorMessage,
  managementWritesAllowed,
  cancelPending,
  onCancelClick,
}: {
  orders: AlternateOrderRecord[];
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | undefined;
  managementWritesAllowed: boolean;
  cancelPending: boolean;
  onCancelClick: (order: AlternateOrderRecord) => void;
}): JSX.Element {
  if (isLoading) {
    return <p className="text-sm text-slate-500">Loading open alternate orders…</p>;
  }
  if (isError) {
    return <p className="text-sm text-red-700">{errorMessage ?? "Could not load open alternate orders."}</p>;
  }
  if (orders.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
        No open alternate orders. Create one to add planned builds to the Configuration queue.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-2 py-2">Order</th>
            <th className="px-2 py-2">SKU</th>
            <th className="px-2 py-2">Target date</th>
            <th className="px-2 py-2">Progress</th>
            <th className="px-2 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id} className="border-b border-slate-100 last:border-b-0">
              <td className="px-2 py-2 tabular-nums text-slate-700">#{order.id}</td>
              <td className="px-2 py-2 font-mono text-slate-900">{order.sku}</td>
              <td className="px-2 py-2 text-slate-600">{formatDate(order.targetDate)}</td>
              <td className="px-2 py-2 text-slate-600">{fulfillmentLabel(order)}</td>
              <td className="px-2 py-2 text-right">
                <ReadOnlyHoverLayer
                  active={!managementWritesAllowed}
                  hoverHint={MANAGEMENT_WRITE_HINT}
                  layout="inline"
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={!managementWritesAllowed || cancelPending}
                    onClick={() => onCancelClick(order)}
                  >
                    Cancel
                  </Button>
                </ReadOnlyHoverLayer>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AlternateOrderCancelConfirmDialog({
  order,
  managementWritesAllowed,
  cancelPending,
  onKeep,
  onConfirmCancel,
}: {
  order: AlternateOrderRecord;
  managementWritesAllowed: boolean;
  cancelPending: boolean;
  onKeep: () => void;
  onConfirmCancel: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 p-4"
      style={{ zIndex: HUB_MODAL_OVERLAY_Z_INDEX + 1 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alternate-order-cancel-confirm-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !cancelPending) {
          onKeep();
        }
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl" onMouseDown={(event) => event.stopPropagation()}>
        <h2 id="alternate-order-cancel-confirm-title" className="text-lg font-semibold text-slate-900">
          Cancel alternate order?
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Cancel order <span className="font-medium text-slate-900">#{order.id}</span> for{" "}
          <span className="font-mono text-slate-900">{order.sku}</span>? It will leave the Configuration queue and no
          longer accept new configured units.
        </p>
        {order.quantityFulfilled > 0 ? (
          <p className="mt-2 text-sm text-amber-800">
            {formatCount(order.quantityFulfilled)} unit
            {order.quantityFulfilled === 1 ? " is" : "s are"} already configured against this order. Those units remain
            on the alternates shelf.
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" disabled={cancelPending} onClick={onKeep}>
            Keep order
          </Button>
          <ReadOnlyHoverLayer active={!managementWritesAllowed} hoverHint={MANAGEMENT_WRITE_HINT} layout="inline">
            <Button
              type="button"
              variant="danger"
              disabled={!managementWritesAllowed || cancelPending}
              isLoading={cancelPending}
              onClick={onConfirmCancel}
            >
              Cancel order
            </Button>
          </ReadOnlyHoverLayer>
        </div>
      </div>
    </div>
  );
}

export function OpenAlternateOrdersModal({
  open,
  onClose,
  onNewOrder,
  hubWritesAllowed,
  manufacturingHubReadOnlyHint,
}: {
  open: boolean;
  onClose: () => void;
  onNewOrder: () => void;
  hubWritesAllowed: boolean;
  manufacturingHubReadOnlyHint: string;
}): JSX.Element | null {
  const {
    managementWritesAllowed,
    cancelConfirm,
    setCancelConfirm,
    openOrdersQuery,
    cancelMutation,
    orders,
  } = useOpenAlternateOrdersState(open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && cancelConfirm == null) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [cancelConfirm, onClose, open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <>
      <div
        {...hubModalOverlayProps(false)}
        className={cn(hubModalOverlayClassName(false), "items-center justify-center")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="open-alternate-orders-modal-title"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 id="open-alternate-orders-modal-title" className="text-lg font-semibold text-slate-900">
              Alternate orders
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Planned builds on the Configuration queue. Cancel here when demand changes; configured units stay on the
              alternates shelf.
            </p>
          </div>
          <div className="max-h-[calc(90vh-10rem)] space-y-3 overflow-y-auto px-5 py-4">
            <OpenAlternateOrdersTable
              orders={orders}
              isLoading={openOrdersQuery.isLoading}
              isError={openOrdersQuery.isError}
              errorMessage={openOrdersQuery.error?.message}
              managementWritesAllowed={managementWritesAllowed}
              cancelPending={cancelMutation.isPending}
              onCancelClick={setCancelConfirm}
            />
            {cancelMutation.isError ? (
              <p className="text-sm text-red-700">{cancelMutation.error.message}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-4">
            <Button type="button" variant="ghost" onClick={onClose}>
              Close
            </Button>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={manufacturingHubReadOnlyHint} layout="inline">
              <Button type="button" onClick={onNewOrder} disabled={!hubWritesAllowed}>
                New alternate order
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        </div>
      </div>

      {cancelConfirm != null ? (
        <AlternateOrderCancelConfirmDialog
          order={cancelConfirm}
          managementWritesAllowed={managementWritesAllowed}
          cancelPending={cancelMutation.isPending}
          onKeep={() => {
            setCancelConfirm(null);
            cancelMutation.reset();
          }}
          onConfirmCancel={() => {
            cancelMutation.mutate({
              alternateOrderId: cancelConfirm.id,
              confirmPartialFulfillment: cancelConfirm.quantityFulfilled > 0,
            });
          }}
        />
      ) : null}
    </>,
    document.body,
  );
}
